import { z } from "zod"
import { correlationIdSchema, tenantIdSchema } from "./identifiers.js"

export const PUBLIC_ASSERTION_ISSUER = "impetusos-bff-public"
export const PUBLIC_ASSERTION_SCOPE = "customer_intake.submit"
export const PUBLIC_ASSERTION_MAX_LIFETIME_SECONDS = 60

const canonicalUuidSchema = z.string().uuid()
const siteNameSchema = z.string().regex(
  /^(?=.{1,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/,
  "Site name must be a canonical lowercase hostname."
)
const requiredText = (maximum: number) => z.string().trim().min(1).max(maximum)

export const publicIntakeContactV1Schema = z.object({
  displayName: requiredText(160),
  email: z.string().trim().email().max(254).nullable(),
  phone: z.string().trim().min(1).max(32).nullable(),
  preferredChannel: z.enum(["email", "sms"]),
  emailConsent: z.boolean(),
  smsConsent: z.boolean(),
}).strict().superRefine((contact, refinement) => {
  if (contact.email === null && contact.phone === null) {
    refinement.addIssue({ code: "custom", message: "A contact channel is required." })
  }
  if (contact.preferredChannel === "email" && (contact.email === null || !contact.emailConsent)) {
    refinement.addIssue({
      code: "custom",
      path: ["preferredChannel"],
      message: "The preferred email channel requires an address and consent.",
    })
  }
  if (contact.preferredChannel === "sms" && (contact.phone === null || !contact.smsConsent)) {
    refinement.addIssue({
      code: "custom",
      path: ["preferredChannel"],
      message: "The preferred SMS channel requires a number and consent.",
    })
  }
})

export const publicIntakeAddressV1Schema = z.object({
  addressLine1: requiredText(240),
  addressLine2: requiredText(240).nullable(),
  city: requiredText(120),
  region: requiredText(120),
  postalCode: requiredText(24),
  country: z.string().trim().length(2).transform((value) => value.toUpperCase()),
}).strict()

const publicIntakeCoreV1Schema = z.object({
  submissionVersion: z.literal(1),
  submissionId: canonicalUuidSchema,
  customerType: z.enum(["residential", "commercial", "property_manager"]),
  displayName: requiredText(160),
  primaryContact: publicIntakeContactV1Schema,
  consentVersion: requiredText(64),
  serviceAddress: publicIntakeAddressV1Schema,
}).strict()

export const publicIntakeRequestV1Schema = publicIntakeCoreV1Schema.extend({
  antiAbuse: z.object({
    formElapsedMs: z.number().int().min(0).max(86_400_000),
    website: z.string().max(200),
  }).strict(),
}).strict()

export const publicIntakeSubmissionV1Schema = publicIntakeCoreV1Schema.extend({
  abuseContextHash: z.string().regex(/^[a-f0-9]{64}$/),
}).strict()

export const publicIntakeResponseV1Schema = z.object({
  responseVersion: z.literal(1),
  status: z.literal("received"),
}).strict()

export const publicAssertionClaimsV1Schema = z.object({
  version: z.literal(1),
  iss: z.literal(PUBLIC_ASSERTION_ISSUER),
  aud: z.string(),
  site: siteNameSchema,
  tenantId: tenantIdSchema,
  scope: z.literal(PUBLIC_ASSERTION_SCOPE),
  requestId: canonicalUuidSchema,
  correlationId: correlationIdSchema,
  jti: canonicalUuidSchema,
  iat: z.number().int().nonnegative().safe(),
  exp: z.number().int().positive().safe(),
}).strict().superRefine((claims, refinement) => {
  if (claims.aud !== `impetusos-frappe-public:${claims.site}`) {
    refinement.addIssue({ code: "custom", path: ["aud"], message: "Audience must bind the site." })
  }
  if (claims.exp <= claims.iat || claims.exp - claims.iat > PUBLIC_ASSERTION_MAX_LIFETIME_SECONDS) {
    refinement.addIssue({ code: "custom", path: ["exp"], message: "Assertion lifetime is invalid." })
  }
})

export type PublicAssertionClaimsV1 = z.infer<typeof publicAssertionClaimsV1Schema>
export type PublicIntakeRequestV1 = z.infer<typeof publicIntakeRequestV1Schema>
export type PublicIntakeResponseV1 = z.infer<typeof publicIntakeResponseV1Schema>
export type PublicIntakeSubmissionV1 = z.infer<typeof publicIntakeSubmissionV1Schema>
